import { Distribution, DistributionParam, GraphNodeType } from "@custom-types/graphs"
import {
  COLLAPSED_GROUP_Z_INDEX,
  DRAGGED_COLLAPSED_GROUP_Z_INDEX,
  DRAGGED_EXPANDED_GROUP_Z_INDEX,
  DRAGGED_NODE_Z_INDEX,
  EXPANDED_GROUP_Z_INDEX,
  NODE_Z_INDEX,
} from "@features/builder/graph/Graph"
import { createEmptyNode, getDefaultNodeDfCpd } from "@features/builder/graph/helpers"
import {
  AppNode,
  ContinuousNode,
  ForecastTableNode,
  GroupNode,
  LagNode,
  MixtureNode,
  NodeType,
  NodesMap,
  ScenarioTableNode,
  TableNode,
} from "@features/builder/graph/types"
import { addEdgeToNode } from "@features/builder/helpers/add-edge"
import { MAX_LAG_VALUE, MIN_LAG_VALUE } from "@features/builder/helpers/consts"
import { removeEdgeFromNode } from "@features/builder/helpers/remove-edge"
import { getRoomLocks } from "@features/builder/hooks/socketIOHooks"
import { linkParse } from "@components/NodeLink/linkHelpers"
import { SocketRoomPrefix } from "@features/builder/types"
import { LAG_LABEL_REGEX, splitLagNodeLabel } from "@utils/node/labelValidation"
import { TKNode } from "@store/services/converter/types"
import logger from "@utils/logger"
import { createLagNodeLabel } from "@utils/node"
import { getParentsList } from "@utils/node/getParentsList"
import { XYPosition } from "@xyflow/react"
import { isEqual } from "lodash"

export const nodeWidth = 90
export const nodeHeight = 30
export const expandedGroupMinWidth = 150
export const expandedGroupMinHeight = 50
export const groupPadding = 20 // multiplication of snapToGrid.size
export const groupTitleHeight = 18

export const createNodesMap = (nodes: AppNode[]): NodesMap => {
  return nodes.reduce((map, node) => ((map[node.id] = node), map), {} as NodesMap)
}

export const getNestedNodeParents = (
  node: AppNode,
  nodesMap: NodesMap,
  callStackSize: number = 0,
): AppNode[] => {
  const parentNode = node.parentId ? nodesMap[node.parentId] : null

  if (!parentNode) return []
  if (callStackSize > 1000) {
    logger.info("Max call stack exceeded. Fallback to empty.")
    return []
  }

  return [parentNode, ...getNestedNodeParents(parentNode, nodesMap, callStackSize + 1)]
}

export const doesNodeHaveLagParent = (node: AppNode, allNodes: AppNode[]) => {
  const parentNodesIds = getParentsList(node)

  return allNodes.some(({ id, type }) => parentNodesIds.includes(id) && type === NodeType.Lag)
}

export const getParentNodes = (node: AppNode, allNodes: AppNode[]) => {
  const nodeMap = createNodesMap(allNodes)

  return getParentsList(node)
    .map((nodeId) => nodeMap[nodeId])
    .filter((node) => node !== undefined)
}

export const getChildrenNodes = (node: AppNode, allNodes: AppNode[]): AppNode[] => {
  return allNodes.filter((n) => getParentsList(n).includes(node.id))
}

export const convertNode = (
  node: Exclude<AppNode, GroupNode>,
  type: Exclude<NodeType, NodeType.Group>,
  allNodes: AppNode[],
  parentsLinkedNodesMap: Map<string, TKNode>,
): AppNode[] => {
  if (node.type === type) return [node]
  switch (type) {
    case NodeType.Table:
      return resetNodeEdges(convertNodeToTableNode(node), allNodes, [], parentsLinkedNodesMap)
    case NodeType.ForecastTable:
      return resetNodeEdges(convertNodeForecastTableNode(node), allNodes, [], parentsLinkedNodesMap)
    case NodeType.ScenarioTable:
      return resetNodeEdges(convertNodeScenarioTableNode(node), allNodes, [], parentsLinkedNodesMap)
  }
  if (
    node.type === NodeType.Table ||
    node.type === NodeType.ScenarioTable ||
    node.type === NodeType.ForecastTable
  ) {
    return resetNodeEdges(convertTableNodeToNode(node, type), allNodes, [], parentsLinkedNodesMap)
  }

  const updatedNode = createEmptyNode(type, node.id, node.position, node.selected) as Exclude<
    AppNode,
    GroupNode | TableNode | ForecastTableNode | ScenarioTableNode
  >
  const originalNode = node as typeof updatedNode
  updatedNode.parentId = originalNode.parentId
  updatedNode.extent = originalNode.extent
  updatedNode.zIndex = originalNode.zIndex
  updatedNode.measured = originalNode.measured
  updatedNode.hidden = originalNode.hidden

  updatedNode.data.nestingLevel = originalNode.data.nestingLevel
  updatedNode.data.label = originalNode.data.label
  updatedNode.data.comments = originalNode.data.comments

  updatedNode.data.metaNode = {
    ...originalNode.data.metaNode,
    Link: !isPseudoNodeType(type) ? originalNode.data.metaNode.Link : "",
  }
  updatedNode.data.originalNode.properties = {
    ...originalNode.data.originalNode.properties,
    df_cpd: getDefaultNodeDfCpd(type),
  }

  if (updatedNode.type === NodeType.Lag) {
    const hasLagParent = doesNodeHaveLagParent(node, allNodes)

    if (hasLagParent) {
      const expression = node.data.originalNode.properties.df_cpd.expression?.[0] ?? ""
      updatedNode.data.originalNode.properties.df_cpd.expression = [expression]
      updatedNode.data.originalNode.properties.a_sample_value = null
    } else {
      const sampleValue = node.data.originalNode.properties.a_sample_value ?? null
      updatedNode.data.originalNode.properties.a_sample_value = sampleValue
      updatedNode.data.originalNode.properties.df_cpd.expression = [""]
    }

    // Lag node label has to include " Lag x" part, add it on converting to lag type
    if (!updatedNode.data.label?.match(LAG_LABEL_REGEX)) {
      const label = createLagNodeLabel(originalNode.data.label, 1)
      updatedNode.data.label = label
      updatedNode.data.metaNode.label = label
    }
  }

  // converted from lag to other type, label has to be modified to remove " Lag x" part
  if (originalNode.type === NodeType.Lag) {
    const label = splitLagNodeLabel(originalNode.data.label).label
    updatedNode.data.label = label
    updatedNode.data.metaNode.label = label
    updatedNode.data.originalNode.properties.a_sample_value = null
  }

  return resetNodeEdges(updatedNode, allNodes, [], parentsLinkedNodesMap)
}

/**
 * All relations between nodes are specific to type of connected nodes.
 * Every parent node type has different impact on child df_cpd, so we have to make sure
 * we are not leaving something that does not makes sense from perspective of new converted node
 * in the df_cpd. Remove and add edges again to make sure df_cpd is correctly set after conversion.
 **/
export const resetNodeEdges = (
  childNode: AppNode,
  allNodes: AppNode[],
  limitToParentNodeIds: string[] = [],
  parentsLinkedNodesMap: Map<string, TKNode>,
): AppNode[] => {
  const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, node]))
  const parentNodes = getParentNodes(childNode, allNodes)
  const parentsToReset =
    limitToParentNodeIds.length === 0
      ? parentNodes
      : parentNodes.filter((node) => limitToParentNodeIds.includes(node.id))

  allNodesUpdatedMap.set(childNode.id, childNode)

  // remove edges
  parentsToReset.forEach((parent) => {
    const updatedChild = allNodesUpdatedMap.get(childNode.id)
    if (!updatedChild) return

    const updatedNodes = removeEdgeFromNode(
      parent,
      updatedChild,
      Array.from(allNodesUpdatedMap.values()),
    )
    updatedNodes.forEach((updatedNode) => allNodesUpdatedMap.set(updatedNode.id, updatedNode))
  })

  // add edges
  parentsToReset.forEach((parent) => {
    const updatedChild = allNodesUpdatedMap.get(childNode.id)
    if (!updatedChild) return

    const parentLinkedNode = parentsLinkedNodesMap.get(parent.id)
    const updatedNodes = addEdgeToNode(
      parent,
      updatedChild,
      Array.from(allNodesUpdatedMap.values()),
      parentLinkedNode,
    )
    updatedNodes.forEach((updatedNode) => allNodesUpdatedMap.set(updatedNode.id, updatedNode))
  })

  // check which nodes has been impacted by that reset and return only these nodes
  const updatedNodes: AppNode[] = []

  for (const node of allNodes) {
    const updatedNode = allNodesUpdatedMap.get(node.id)
    const hasChanged = !isEqual(node, updatedNode)

    if (updatedNode && hasChanged) updatedNodes.push(updatedNode)
  }

  return updatedNodes
}

export const convertNodeForecastTableNode = (
  node: Exclude<AppNode, GroupNode>,
): ForecastTableNode => {
  if (node.type === NodeType.ForecastTable) return node

  const label =
    node.type === NodeType.Lag ? splitLagNodeLabel(node.data.label).label : node.data.label ?? ""
  return {
    ...node,
    type: NodeType.ForecastTable,
    data: {
      comments: node.data.comments,
      metaNode: {
        ...node.data.metaNode,
        label,
      },
      label,
      nestingLevel: node.data.nestingLevel,
      originalAttribute: {
        type: GraphNodeType.FORECAST_TABLE,
        name: node.id,
        properties: {
          index: ["row1"],
          columns: ["col1"],
          data: [[""]],
          parents_lst: [],
        },
      },
    },
  }
}

export const convertNodeScenarioTableNode = (
  node: Exclude<AppNode, GroupNode>,
): ScenarioTableNode => {
  if (node.type === NodeType.ScenarioTable) return node

  const label =
    node.type === NodeType.Lag ? splitLagNodeLabel(node.data.label).label : node.data.label ?? ""
  return {
    ...node,
    type: NodeType.ScenarioTable,
    data: {
      comments: node.data.comments,
      metaNode: {
        ...node.data.metaNode,
        label,
      },
      label,
      nestingLevel: node.data.nestingLevel,
      originalAttribute: {
        type: GraphNodeType.SCENARIO_TABLE,
        name: node.id,
        properties: {
          index: ["row1"],
          columns: ["col1"],
          data: [[""]],
          parents_lst: [],
        },
      },
    },
  }
}

export const convertNodeToTableNode = (node: Exclude<AppNode, GroupNode>): TableNode => {
  if (node.type === NodeType.Table) return node

  const label =
    node.type === NodeType.Lag ? splitLagNodeLabel(node.data.label).label : node.data.label ?? ""
  return {
    ...node,
    type: NodeType.Table,
    data: {
      comments: node.data.comments,
      metaNode: {
        ...node.data.metaNode,
        label,
      },
      label,
      nestingLevel: node.data.nestingLevel,
      originalAttribute: {
        type: GraphNodeType.TABLE,
        name: node.id,
        properties: {
          index: ["row1", "row2"],
          columns: ["col1", "col2"],
          data: [
            ["0", "0"],
            ["0", "0"],
          ],
          parents_lst:
            "originalNode" in node.data ? node.data.originalNode.properties.parents_lst : [],
        },
      },
    },
  }
}

export const convertTableNodeToNode = (
  node: TableNode | ScenarioTableNode | ForecastTableNode,
  type: Exclude<
    NodeType,
    NodeType.Table | NodeType.Group | NodeType.ForecastTable | NodeType.ScenarioTable
  >,
): Exclude<AppNode, TableNode | GroupNode> => {
  const updatedMetaNode = structuredClone(node.data.metaNode)
  updatedMetaNode.Link = !isPseudoNodeType(type) ? updatedMetaNode.Link : ""

  return {
    ...node,
    type: type,
    data: {
      comments: node.data.comments,
      metaNode: updatedMetaNode,
      label: node.data.label,
      nestingLevel: node.data.nestingLevel,
      originalNode: {
        name: node.id,
        type: type as unknown as GraphNodeType,
        properties: {
          df_cpd: getDefaultNodeDfCpd(type),
          a_sample_value: null,
          attributes_lst: [],
          parents_lst: node.data.originalAttribute.properties.parents_lst ?? [],
        },
      },
    },
  }
}

export const shouldContinuousNodeBeConvertedToMixtureNode = (
  node: ContinuousNode,
  allNodes: AppNode[],
): boolean => {
  const parentNodes = getParentNodes(node, allNodes)

  return parentNodes.some((node) =>
    [NodeType.Categorical, NodeType.Discrete].includes(node.type as NodeType),
  )
}

export const shouldMixtureNodeBeConvertedToContinuousNode = (
  node: MixtureNode,
  allNodes: AppNode[],
): boolean => {
  const parentNodes = getParentNodes(node, allNodes)

  return !parentNodes.some((node) =>
    [NodeType.Categorical, NodeType.Discrete].includes(node?.type as NodeType),
  )
}

export const convertContinuousNodeToMixtureNode = (node: ContinuousNode): MixtureNode => {
  return {
    ...node,
    type: NodeType.Mixture,
    data: {
      ...node.data,
      originalNode: {
        ...node.data.originalNode,
        type: GraphNodeType.MIXTURE,
      },
    },
  }
}

export const convertMixtureNodeToContinuousNode = (node: MixtureNode): ContinuousNode => {
  return {
    ...node,
    type: NodeType.Continuous,
    data: {
      ...node.data,
      originalNode: {
        ...node.data.originalNode,
        type: GraphNodeType.CONTINOUS,
      },
    },
  }
}

export const doesNodeNeedTypeUpdate = (node: AppNode, allNodes: AppNode[]): boolean => {
  const needsToBeUpdatedToMixture =
    node.type === NodeType.Continuous &&
    shouldContinuousNodeBeConvertedToMixtureNode(node, allNodes)

  const needsToBeUpdatedToContinuous =
    node.type === NodeType.Mixture && shouldMixtureNodeBeConvertedToContinuousNode(node, allNodes)

  return needsToBeUpdatedToMixture || needsToBeUpdatedToContinuous
}

export const updateNodeType = (node: AppNode, allNodes: AppNode[]): AppNode => {
  if (
    node.type === NodeType.Continuous &&
    shouldContinuousNodeBeConvertedToMixtureNode(node, allNodes)
  ) {
    return convertContinuousNodeToMixtureNode(node)
  }

  if (
    node.type === NodeType.Mixture &&
    shouldMixtureNodeBeConvertedToContinuousNode(node, allNodes)
  ) {
    return convertMixtureNodeToContinuousNode(node)
  }

  return node
}

export const getRelativePosition = (
  absolutePosition1: XYPosition,
  absolutePosition2: XYPosition,
): XYPosition => {
  return {
    x: absolutePosition1.x - absolutePosition2.x,
    y: absolutePosition1.y - absolutePosition2.y,
  }
}

/**
Compare function to easily combine with .sort() or .toSorted() to sort groups by nesting level.

The most nested group will be first.
*/
export const sortGroupsByNestingLevel = (groupA: GroupNode, groupB: GroupNode) => {
  return groupA.data.nestingLevel > groupB.data.nestingLevel ? -1 : 1
}

export const getNodeZIndex = (node: AppNode, options?: { isDragging?: boolean }) => {
  if (options?.isDragging) {
    if (node.type === NodeType.Group) {
      return node.data.metaGroup.isCollapsed
        ? DRAGGED_COLLAPSED_GROUP_Z_INDEX
        : DRAGGED_EXPANDED_GROUP_Z_INDEX
    }

    return DRAGGED_NODE_Z_INDEX
  }

  if (node.type === NodeType.Group) {
    return node.data.metaGroup.isCollapsed ? COLLAPSED_GROUP_Z_INDEX : EXPANDED_GROUP_Z_INDEX
  }

  return NODE_Z_INDEX
}

export const getNodeLock = (graphId: string, nodeId: string) => {
  return getRoomLocks(`${SocketRoomPrefix.CRDT}-${graphId}`, nodeId)
}

/**
 * Calculate possible min and max lag value for given LAG node based on its parents and children LAG nodes.
 * Lag value has to be between max lag value of LAG children nodes AND min lag value of LAG parents
 *
 * @param node Lag node to calculate possible min and max lag value for
 * @param allNodes All nodes in the graph
 *
 * @return Object with min and max lag value
 */
export const getLagNodePossibleMinMaxLagValue = (node: LagNode, allNodes: AppNode[]) => {
  return {
    max: getLagNodeMaximalLagValue(node, allNodes),
    min: getLagNodeMinimalLagValue(node, allNodes),
  }
}

export const getLagNodeMinimalLagValue = (node: LagNode, allNodes: AppNode[]) => {
  const childrenLagValues = getChildrenNodes(node, allNodes)
    .filter((n) => n.type === NodeType.Lag)
    .map((n) => splitLagNodeLabel(n.data.label).lagValue)
  const maxChildrenLagValue = Math.max(MIN_LAG_VALUE, ...childrenLagValues)

  return maxChildrenLagValue
}

export const getLagNodeMaximalLagValue = (node: LagNode, allNodes: AppNode[]) => {
  const parentLagValues = getParentNodes(node, allNodes)
    .filter((n) => n.type === NodeType.Lag)
    .map((n) => splitLagNodeLabel(n.data.label).lagValue)
  const minParentLagValue = Math.min(...parentLagValues, MAX_LAG_VALUE)

  return minParentLagValue
}

export type DistributionDefaultValues = {
  [K in Distribution]?: {
    [P in DistributionParam]?: string | null
  }
}

export const discreteDistributionDefaultValues: DistributionDefaultValues = {
  [Distribution.Binomial]: {
    [DistributionParam.n]: "1",
    [DistributionParam.p]: "0.5",
    [DistributionParam.loc]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Geometric]: {
    [DistributionParam.p]: "0.5",
    [DistributionParam.loc]: "0",
    [DistributionParam.ubound]: "4",
    [DistributionParam.lbound]: null,
  },
  [Distribution.Hypergeometric]: {}, // NOT SUPPORTED
  [Distribution.Logseries]: {
    [DistributionParam.p]: "0.5",
    [DistributionParam.loc]: "0",
    [DistributionParam.ubound]: "4",
    [DistributionParam.lbound]: null,
  },
  [Distribution.Poisson]: {
    [DistributionParam.mu]: "0.1",
    [DistributionParam.loc]: "0",
    [DistributionParam.ubound]: "4",
    [DistributionParam.lbound]: null,
  },
}

export const continuousDistributionDefaultValues: DistributionDefaultValues = {
  [Distribution.Weibull]: {
    [DistributionParam.c]: "3",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Wald]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Vonmises]: {
    [DistributionParam.kappa]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Uniform]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Triangular]: {
    [DistributionParam.c]: "0.5",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.T]: {
    [DistributionParam.df]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Rayleigh]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Power]: {
    [DistributionParam.a]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Pareto]: {
    [DistributionParam.b]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Normal]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.NonCentralF]: {
    [DistributionParam.dfn]: "1",
    [DistributionParam.dfd]: "1",
    [DistributionParam.nc]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.NonCentralChisquare]: {
    [DistributionParam.df]: "1",
    [DistributionParam.nc]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Lognormal]: {
    [DistributionParam.s]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Logistic]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Laplace]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Gumbel]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Gamma]: {
    [DistributionParam.a]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.F]: {
    [DistributionParam.dfn]: "1",
    [DistributionParam.dfd]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Exponential]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Chisquare]: {
    [DistributionParam.df]: "1",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Cauchy]: {
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
  [Distribution.Beta]: {
    [DistributionParam.a]: "2",
    [DistributionParam.b]: "2",
    [DistributionParam.loc]: "1",
    [DistributionParam.scale]: "0",
    [DistributionParam.ubound]: null,
    [DistributionParam.lbound]: null,
  },
}

export const LINKABLE_NODE_TYPES = [
  NodeType.Continuous,
  NodeType.Mixture,
  NodeType.Discrete,
  NodeType.Deterministic,
  NodeType.Categorical,
  NodeType.Table,
]

export const NOT_TAGGABLE_NODE_TYPES = [
  NodeType.Group,
  NodeType.Empty,
  NodeType.ForecastTable,
  NodeType.ScenarioTable,
]

export const isTaggableNodeType = (type: NodeType) => !NOT_TAGGABLE_NODE_TYPES.includes(type)

export const distributionDefaultValues: DistributionDefaultValues = {
  ...discreteDistributionDefaultValues,
  ...continuousDistributionDefaultValues,
}

export { isNodeTypeEqual } from "@features/builder/helpers/nodeTypeUtils"

export const isPseudoNodeType = (type: NodeType) => {
  return [NodeType.Scenario, NodeType.Forecast, NodeType.Lag].includes(type)
}

export const getGraphLinkedNodesPerVersion = (
  allNodes: AppNode[],
  graphId: string,
  excludeNodeId?: string,
) => {
  const linkedNodesPerVersion: Record<string, AppNode[]> = {}

  if (!graphId) return linkedNodesPerVersion

  allNodes.forEach((node) => {
    if (node.id === excludeNodeId) return

    const link = "metaNode" in node.data && node.data.metaNode.Link ? node.data.metaNode.Link : ""
    if (!link) return

    const parsedLink = linkParse(link)
    if (!parsedLink) return

    const { org, div, prod, family, version } = parsedLink
    if (`${org}#${div}#${prod}#${family}` !== graphId) return

    const versionMap = linkedNodesPerVersion[version]
    if (versionMap) {
      versionMap.push(node)
    } else {
      linkedNodesPerVersion[version] = [node]
    }
  })

  return linkedNodesPerVersion
}

export const getNodeLink = (node: AppNode) => {
  return "metaNode" in node.data ? node.data.metaNode.Link : undefined
}
